import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { fetchQuestions } from './fetchQuestions';
import { addQuestion } from './addQuestion';
import { updateQuestion } from './updateQuestion';
import { difference } from 'src/utils/objectUtil';
import { FilterState } from 'src/domains/layouts/shared/filter/Filter.state';
import { filtersGlobal } from 'src/components/GlobalPageV2/GlobalPageV2.types';

export class CsvReadItem {
    public constructor(
        public readonly id: string,
        public name: string,
        public active: boolean,
        public fileName: string | null,
        public uploadedAt: string | null,
        public possibleAnswers: string[] | null
    ) {}
}

export class CsvReadFile {
    public constructor(
        public fileName: string,
        public uploadedAt: string,
        public possibleAnswers: string[] = []
    ) {}

    public static fromCsvReadItem(question: CsvReadItem): CsvReadFile | null {
        const { fileName, uploadedAt, possibleAnswers } = question;
        if (fileName !== null && uploadedAt !== null && possibleAnswers !== null) {
            return new CsvReadFile(fileName, uploadedAt, possibleAnswers);
        }
        return null;
    }
}

export class PromotionQuestionsState {
    public readonly maxQuestionsEnabled: number = 3;

    @observable public readonly questionsResource: Resource<Array<CsvReadItem>>;
    @observable.ref public currentQuestion: CsvReadItem | null = null;
    @observable.ref public recentlyUpdatedQuestion: CsvReadItem | null = null;
    @observable public isCreateMode: boolean = false;
    @observable public areQuestionsLoading: boolean = false;
    @observable public filters;

    public constructor() {
        this.questionsResource = new Resource(async () => {
            this.areQuestionsLoading = true;
            const questions = await fetchQuestions();
            // after update/add new question, we can't be sure that received questions list is also updated (i.e. due to some DB delay)
            // so here is the reason why we mutate the response
            if (this.recentlyUpdatedQuestion !== null) {
                const questionToUpdate = questions.find((question) => question.id === this.recentlyUpdatedQuestion?.id);
                if (questionToUpdate === undefined) {
                    questions.push(this.recentlyUpdatedQuestion);
                } else {
                    Object.assign(questionToUpdate, this.recentlyUpdatedQuestion);
                }
            }
            this.setRecentlyUpdatedQuestion(null);
            this.areQuestionsLoading = false;
            return questions;
        });

        this.filters = new FilterState(filtersGlobal, 'show-all');
        makeAutoObservable(this);
    }

    @computed public get questions(): Array<CsvReadItem> {
        const questions = this.questionsResource.get();
        if (questions.type === 'ready') {
            return questions.value;
        }
        return [];
    }

    @computed public get sortedQuestions(): Array<CsvReadItem> {
        return this.questions.sort((a, b) => {
            return a.active === b.active ? 0 : a.active ? -1 : 1;
        });
    }

    @computed public get sortedQuestionsV2(): Array<CsvReadItem> {
        return this.questions.sort((a, b) => {
            if (a.uploadedAt !== null && b.uploadedAt !== null) {
                return a.uploadedAt === b.uploadedAt ? 0 : a.uploadedAt > b.uploadedAt ? -1 : 1;
            }
            if (a.uploadedAt === null) return 1;
            if (b.uploadedAt === null) return -1;
            return 0;
        });
    }

    @computed public get questionsForViewV2(): Array<CsvReadItem> {
        if (this.filters.activeFilter === 'show-all') {
            return this.sortedQuestionsV2;
        }
        if (this.filters.activeFilter === 'unpublished') {
            return this.sortedQuestionsV2.filter((item) => item.active === false);
        }
        return this.sortedQuestionsV2.filter((item) => item.active === true);
    }

    @action public setCurrentQuestion = (question: CsvReadItem | null): void => {
        this.currentQuestion = question;
        this.isCreateMode = false;
    };

    @action public setRecentlyUpdatedQuestion = (question: CsvReadItem | null): void => {
        this.recentlyUpdatedQuestion = question;
    };

    @action public showCreateNewForm = (value: boolean): void => {
        this.currentQuestion = null;
        this.isCreateMode = value;
    };

    @computed public get isSetActiveAllowed(): boolean {
        const questionToIgnore = this.currentQuestion;
        const activeQuestions = this.questions
            .filter((question) => question != questionToIgnore)
            .filter((question) => question.active);
        return activeQuestions.length < this.maxQuestionsEnabled;
    }

    @action public submitQuestion = async (question: Partial<CsvReadItem>): Promise<CsvReadItem | null> => {
        const existingQuestion = this.questions.find((q) => q.id === question.id);
        const payload = this.getPayloadToSend(question, existingQuestion);
        let response: CsvReadItem;
        try {
            if (existingQuestion === undefined) {
                response = await addQuestion(question);
            } else {
                response = await updateQuestion(existingQuestion.id, payload);
            }
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action public refreshQuestions = async (): Promise<void> => {
        await this.questionsResource.refresh();
    };

    private getPayloadToSend(
        formQuestion: Partial<CsvReadItem>,
        modelQuestion: CsvReadItem | null = null
    ): Partial<CsvReadItem> {
        if (modelQuestion !== null && modelQuestion.id === formQuestion.id) {
            const payload = difference(modelQuestion, formQuestion);
            return payload;
        }
        return formQuestion;
    }

    public static createForContext(): PromotionQuestionsState {
        return new PromotionQuestionsState();
    }
}
